/**
 * WorkflowLabelPreview — the 60×50 mm on-screen render of a Prep or
 * Retail label. Mirrors the TSPL layout emitted by the backend so what
 * staff see matches what the Tecor TR-422B prints. Honours the per-site
 * layout template (`preview.template` = ordered list of visible field
 * keys). Used both as the live preview and inside PrintContext for the
 * browser-print fallback.
 *
 * `preview.type` = "prep" | "retail".
 */
import { QRCodeSVG } from "qrcode.react";
import { allergenLabel, splitAllergenSegments } from "@/lib/allergens";
import { NutritionPanel } from "@/components/NutritionPanel";
import { DEFAULT_PREP_TEMPLATE, DEFAULT_RETAIL_TEMPLATE } from "@/lib/labelFields";

function allergenLabelSafe(a) {
  return allergenLabel(a) || a;
}

export default function WorkflowLabelPreview({ preview, realSize = true }) {
  if (!preview) return null;
  const isRetail = preview.type === "retail";
  const template =
    preview.template || (isRetail ? DEFAULT_RETAIL_TEMPLATE : DEFAULT_PREP_TEMPLATE);
  const cardStyle = realSize
    ? { width: "60mm", minHeight: "50mm" }
    : { width: "300px", minHeight: "250px" };

  const renderPrepField = (key) => {
    switch (key) {
      case "prep_date":
        return <div key={key}><b>PREP:</b> {preview.prep_date}</div>;
      case "use_by_date":
        return <div key={key} className="text-sm"><b>USE BY:</b> {preview.use_by_date}</div>;
      case "storage":
        return <div key={key}><b>STORE:</b> {preview.storage}</div>;
      case "staff_id":
        return <div key={key}><b>STAFF:</b> {preview.staff_id}</div>;
      default:
        return null;
    }
  };

  return (
    <div className="flex justify-center" data-print-scope="workflow">
      <div
        className="label-card border-2 border-dashed border-eucalyptus-700/40"
        style={cardStyle}
        data-testid={`workflow-label-${preview.type}`}
      >
        <div className="font-mono text-[10px] tracking-wider uppercase text-center border-b border-black pb-1">
          {preview.site || "HACCP AU"}
        </div>

        <div className="mt-1 text-center">
          <div className="font-display font-extrabold text-base leading-tight uppercase">
            {preview.product || "\u2014"}
          </div>
        </div>

        {isRetail ? (
          <>
          {template.map((key) => {
            if (key === "price" && preview.price != null && preview.price !== "") {
              return (
                <div key={key} className="text-center font-display font-extrabold text-2xl mt-1" data-testid="wf-price">
                  ${Number(preview.price).toFixed(2)}
                </div>
              );
            }
            if (key === "best_before" && preview.best_before) {
              return <div key={key} className="mt-2 font-mono text-[11px]"><b>BEST BEFORE:</b> {preview.best_before}</div>;
            }
            if (key === "ingredients" && preview.ingredients) {
              const segs = splitAllergenSegments(preview.ingredients, preview.allergens || []);
              return (
                <div key={key} className="mt-1 font-mono text-[9px] leading-snug" style={{ pageBreakInside: "avoid" }} data-testid="wf-preview-ingredients">
                  <span className="font-bold">INGREDIENTS: </span>
                  {segs.map((s, i) => (
                    s.bold
                      ? <strong key={i} className="font-bold">{s.text}</strong>
                      : <span key={i}>{s.text}</span>
                  ))}
                </div>
              );
            }
            if (key === "allergens" && preview.allergens?.length > 0) {
              return (
                <div key={key} className="mt-1 border-t border-black pt-1" style={{ pageBreakInside: "avoid" }}>
                  <div className="font-mono text-[9px] font-bold">ALLERGENS:</div>
                  <div className="font-display font-extrabold text-[12px] uppercase leading-tight">
                    {preview.allergens.map(allergenLabelSafe).join(", ")}
                  </div>
                </div>
              );
            }
            if (key === "barcode" && preview.barcode) {
              return (
                <div key={key} className="mt-2 flex flex-col items-center" data-testid="wf-barcode" style={{ pageBreakInside: "avoid" }}>
                  <QRCodeSVG value={preview.barcode} size={64} level="M" marginSize={1} aria-label="Product barcode" />
                  <div className="mt-1 font-mono text-[10px] tracking-widest">{preview.barcode}</div>
                </div>
              );
            }
            return null;
          })}
          {preview.nutrition?.enabled && (
            <div data-testid="wf-preview-nip" style={{ pageBreakInside: "avoid" }}>
              <NutritionPanel nutrition={preview.nutrition} />
            </div>
          )}
          {preview.trust_url && (
            <div className="mt-2 flex flex-col items-center border-t border-black pt-1" data-testid="wf-trust-qr" style={{ pageBreakInside: "avoid" }}>
              <div className="font-mono text-[9px] font-bold tracking-wider">SCAN: OUR FOOD SAFETY</div>
              <QRCodeSVG value={preview.trust_url} size={56} level="M" marginSize={1} aria-label="Food safety trust page" className="mt-1" />
            </div>
          )}
          </>
        ) : (
          <>
            <div className="mt-2 font-mono text-[11px] leading-snug">
              {template.filter((k) => k !== "allergens").map(renderPrepField)}
            </div>
            {template.includes("allergens") && preview.allergens?.length > 0 && (
              <div className="mt-2 border-t border-black pt-1 font-mono text-[10px]" style={{ pageBreakInside: "avoid" }}>
                <div className="font-bold">CONTAINS:</div>
                <div className="uppercase">{preview.allergens.map(allergenLabelSafe).join(", ")}</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
